import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CartonType, CategoryFacet } from '../../services/api/cartonTypeService';
import {
  loadCartonTypeCatalog,
  createCartonType,
  updateCartonType,
  deactivateCartonType,
  reactivateCartonType,
  loadCartonTypeReferences,
} from '../thunks/cartonTypeCatalogThunks';

/** Filter state mirrored from URL query params (q, category, showInactive, page, pageSize). */
export interface CartonTypeCatalogFilters {
  q: string;
  category: string[];
  showInactive: boolean;
  page: number;
  pageSize: number;
}

export type DrawerMode = 'closed' | 'read' | 'edit';

export interface CartonTypeReferenceCacheEntry {
  count: number;
  profileIds: number[];
  /** Epoch ms when fetched — used by the slice consumer to age-out stale entries. */
  loadedAt: number;
}

export interface CartonTypeCatalogState {
  items: CartonType[];
  total: number;
  availableCategories: CategoryFacet[];

  filters: CartonTypeCatalogFilters;

  status: 'idle' | 'loading' | 'success' | 'failure';
  lastError: string | null;

  drawerCartonId: number | null;
  drawerMode: DrawerMode;

  /** Reference-count cache keyed by carton-type id. Populated lazily. */
  referencesCache: Record<number, CartonTypeReferenceCacheEntry>;
}

const initialState: CartonTypeCatalogState = {
  items: [],
  total: 0,
  availableCategories: [],

  filters: {
    q: '',
    category: [],
    showInactive: false,
    page: 1,
    pageSize: 50,
  },

  status: 'idle',
  lastError: null,

  drawerCartonId: null,
  drawerMode: 'closed',

  referencesCache: {},
};

const cartonTypeCatalogSlice = createSlice({
  name: 'cartonTypeCatalog',
  initialState,
  reducers: {
    setFilters: <K extends keyof CartonTypeCatalogFilters>(
      state: CartonTypeCatalogState,
      action: PayloadAction<{ key: K; value: CartonTypeCatalogFilters[K] }>
    ) => {
      state.filters[action.payload.key] = action.payload.value;
      // Filter changes reset paging to first page (mainstream pattern).
      if (action.payload.key !== 'page' && action.payload.key !== 'pageSize') {
        state.filters.page = 1;
      }
    },
    clearError: (state) => {
      state.lastError = null;
    },
    openDrawer: (state, action: PayloadAction<{ cartonId: number; mode?: DrawerMode }>) => {
      state.drawerCartonId = action.payload.cartonId;
      state.drawerMode = action.payload.mode ?? 'read';
    },
    closeDrawer: (state) => {
      state.drawerCartonId = null;
      state.drawerMode = 'closed';
    },
    setDrawerMode: (state, action: PayloadAction<DrawerMode>) => {
      state.drawerMode = action.payload;
    },
    invalidateReferencesCache: (state, action: PayloadAction<{ cartonId?: number }>) => {
      if (action.payload.cartonId != null) {
        delete state.referencesCache[action.payload.cartonId];
      } else {
        state.referencesCache = {};
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCartonTypeCatalog.pending, (state) => {
        state.status = 'loading';
        state.lastError = null;
      })
      .addCase(loadCartonTypeCatalog.fulfilled, (state, action) => {
        state.status = 'success';
        state.items = action.payload.items;
        state.total = action.payload.total ?? action.payload.items.length;
        state.availableCategories = action.payload.availableCategories;
      })
      .addCase(loadCartonTypeCatalog.rejected, (state, action) => {
        state.status = 'failure';
        state.lastError = (action.payload as string) ?? action.error.message ?? 'Failed to load carton types';
      });

    // Successful writes invalidate the references cache (relationships may
    // have changed) and prompt a list refresh on the consumer side.
    builder
      .addCase(createCartonType.fulfilled, (state) => {
        state.referencesCache = {};
      })
      .addCase(updateCartonType.fulfilled, (state) => {
        state.referencesCache = {};
      })
      .addCase(deactivateCartonType.fulfilled, (state, action) => {
        state.referencesCache = {};
        // Reflect is_active=false in any cached row so the table updates
        // optimistically before the next page fetch lands.
        const idx = state.items.findIndex((x) => x.id === action.payload.id);
        if (idx >= 0) {
          state.items[idx] = { ...state.items[idx], isActive: false };
        }
      })
      .addCase(reactivateCartonType.fulfilled, (state, action) => {
        state.referencesCache = {};
        const idx = state.items.findIndex((x) => x.id === action.payload.id);
        if (idx >= 0) {
          state.items[idx] = { ...state.items[idx], isActive: true };
        }
      });

    builder.addCase(loadCartonTypeReferences.fulfilled, (state, action) => {
      state.referencesCache[action.payload.cartonTypeId] = {
        count: action.payload.activeApprovedListCount,
        profileIds: action.payload.activeProfileIds,
        loadedAt: Date.now(),
      };
    });
  },
});

export const {
  setFilters,
  clearError,
  openDrawer,
  closeDrawer,
  setDrawerMode,
  invalidateReferencesCache,
} = cartonTypeCatalogSlice.actions;
export default cartonTypeCatalogSlice.reducer;
